import { useEffect } from "react";
import useReaderStore from "../context/readerStore";
import useActionGuard from "./useActionGuard";
import { isFeatureAllowed } from "../services/drmPolicyService";
import { showFeatureNotAllowedNotification } from "../services/notificationService";

/**
 * Custom hook to handle global keyboard shortcuts
 * Currently handles:
 * - Escape: Close all open dialogs, modals, and popups
 * - B: Toggle bookmarks panel
 * - T: Toggle table of contents
 * - N: Toggle notes panel
 * - Alt + H (Windows) / Option + H (Mac): Toggle highlight mode
 * - Ctrl/Cmd + F: Toggle search sidebar
 * - Ctrl + S: Toggle bookmark for current page
 * - Home/End: Jump to first/last page
 * - Ctrl + \: Fit to width (PDF only)
 * - Ctrl + /: Fit to page (PDF only)
 */
export const useKeyboardShortcuts = () => {
  const {
    isTocSidebarOpen,
    isBookmarksSidebarOpen,
    isNotesSidebarOpen,
    isSearchSidebarOpen,
    isPagesSidebarOpen,
    isReadingAssistantSidebarOpen,
    isPublicationDetailsSidebarOpen,
    toggleTocSidebar,
    toggleBookmarksSidebar,
    toggleNotesSidebar,
    toggleSearchSidebar,
    togglePagesSidebar,
    toggleReadingAssistantSidebar,
    togglePublicationDetailsSidebar,
    toggleHighlightEnabled,
    document,
    documentType,
    metadata,
    totalPages,
    setCurrentPage,
    drmPolicy,
  } = useReaderStore();

  const { run: dispatchBookmarkToggle } = useActionGuard(() => {
    window.dispatchEvent(new CustomEvent("reader:toggle-bookmark"));
  });

  useEffect(() => {
    const handleKeyDown = (event) => {
      const activeElement = window.document.activeElement;
      const isTypingInInput =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.contentEditable === "true");

      // Also check if note input overlay is visible
      const noteInputOverlay = window.document.querySelector(
        ".note-input-overlay",
      );
      const isNoteInputVisible =
        noteInputOverlay && noteInputOverlay.offsetParent !== null;

      // Allow Cmd/Ctrl+F to work even when the search input is focused.
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key.toLowerCase() === "f" &&
        document
      ) {
        event.preventDefault();
        if (isFeatureAllowed(drmPolicy, "search")) {
          toggleSearchSidebar();
        } else {
          showFeatureNotAllowedNotification("search");
        }
        return;
      }

      if (isTypingInInput || isNoteInputVisible) {
        return;
      }

      // Handle Escape key - close all open modals/sidebars
      if (event.key === "Escape") {
        // Only trigger if document is loaded
        if (!document) return;

        // Close all open sidebars
        if (isTocSidebarOpen) {
          toggleTocSidebar();
        }
        if (isBookmarksSidebarOpen) {
          toggleBookmarksSidebar();
        }
        if (isNotesSidebarOpen) {
          toggleNotesSidebar();
        }
        if (isSearchSidebarOpen) {
          toggleSearchSidebar();
        }
        if (isPagesSidebarOpen) {
          togglePagesSidebar();
        }
        if (isReadingAssistantSidebarOpen) {
          toggleReadingAssistantSidebar();
        }
        if (isPublicationDetailsSidebarOpen) {
          togglePublicationDetailsSidebar();
        }
      }

      // Alt + H (Win) / Option + H (Mac): Toggle highlight mode
      // On macOS, Option+H produces '˙' instead of 'h', so we also
      // check event.code ("KeyH") which reflects the physical key.
      if (
        event.altKey &&
        (event.key.toLowerCase() === "h" || event.code === "KeyH") &&
        document
      ) {
        event.preventDefault();
        if (isFeatureAllowed(drmPolicy, "highlights")) {
          toggleHighlightEnabled();
        } else {
          showFeatureNotAllowedNotification("highlights");
        }
      }

      // B: Toggle bookmarks panel (only when document loaded)
      if (
        event.key.toLowerCase() === "b" &&
        document &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        event.preventDefault();
        if (isFeatureAllowed(drmPolicy, "bookmarks")) {
          toggleBookmarksSidebar();
        } else {
          showFeatureNotAllowedNotification("bookmarks");
        }
      }
      // T: Toggle table of contents (only when document loaded)
      if (
        event.key.toLowerCase() === "t" &&
        document &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        event.preventDefault();
        toggleTocSidebar();
      }

      // N: Toggle notes panel (only when document loaded)
      if (
        event.key.toLowerCase() === "n" &&
        document &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        event.preventDefault();
        if (isFeatureAllowed(drmPolicy, "notes")) {
          toggleNotesSidebar();
        } else {
          showFeatureNotAllowedNotification("notes");
        }
      }
      const resolveMaxPage = () => {
        if (!document) return 0;
        if (documentType === "pdf") {
          return Number(totalPages || document?.numPages || 0);
        }
        const metaTotal = Number(metadata?.total_pages || 0);
        if (metaTotal > 0) return metaTotal;
        const spineLength =
          document?.spine?.length || document?.spine?.spineItems?.length || 0;
        return Number(spineLength || 0);
      };

      const goToPage = (page) => {
        if (!page || !document) return;
        if (documentType === "pdf") {
          window.dispatchEvent(
            new CustomEvent("reader:go-to-page", { detail: { page } }),
          );
          return;
        }
        setCurrentPage(page);
      };

      // Home/End: Jump to first/last page
      if (event.key === "Home" || event.key === "End") {
        const maxPage = resolveMaxPage();
        if (!maxPage) return;
        event.preventDefault();
        const targetPage = event.key === "Home" ? 1 : maxPage;
        goToPage(targetPage);
      }

      // Ctrl + \: Fit to width (PDF only)
      if ((event.ctrlKey || event.metaKey) && event.key === "\\" && document) {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("reader:fit-to-width"));
      }

      // Ctrl + /: Fit to page (PDF only)
      if ((event.ctrlKey || event.metaKey) && event.key === "/" && document) {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("reader:fit-to-page"));
      }

      // Ctrl + S: Toggle bookmark for current page
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key.toLowerCase() === "s" &&
        document
      ) {
        event.preventDefault();
        if (isFeatureAllowed(drmPolicy, "bookmarks")) {
          dispatchBookmarkToggle();
        } else {
          showFeatureNotAllowedNotification("bookmarks");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    isTocSidebarOpen,
    isBookmarksSidebarOpen,
    isNotesSidebarOpen,
    isSearchSidebarOpen,
    isPagesSidebarOpen,
    isReadingAssistantSidebarOpen,
    isPublicationDetailsSidebarOpen,
    toggleTocSidebar,
    toggleBookmarksSidebar,
    toggleNotesSidebar,
    toggleSearchSidebar,
    togglePagesSidebar,
    toggleReadingAssistantSidebar,
    togglePublicationDetailsSidebar,
    toggleHighlightEnabled,
    document,
    documentType,
    metadata,
    totalPages,
    setCurrentPage,
    dispatchBookmarkToggle,
    drmPolicy,
  ]);
};

export default useKeyboardShortcuts;
